import type { DocumentAvailability, DocumentClassification, DocumentView } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { errorMessage } from '../lib/api'
import { DOCUMENT_MIME_TYPES, type UploadProgress, useAccessDocument, useDeleteDocument, useDocuments, useUploadDocument } from '../lib/documents'
import { formatDate, humanize } from '../lib/format'
import { Alert, Button, Card, Field, Input, Mono, Select } from './ui'

type Audience = DocumentView['audience']

/** Only what the API lets this audience upload (apps/api/src/auth/document-projections.ts). */
const UPLOADABLE: Record<Audience, { value: DocumentClassification; label: string }[]> = {
  dealer: [{ value: 'transaction', label: 'Transaction (visible to buyer and Tex)' }],
  borrower: [
    { value: 'borrower_private', label: 'Private — only you and Tex can see it' },
    { value: 'transaction', label: 'Transaction (visible to the dealership too)' },
  ],
  tex: [
    { value: 'transaction', label: 'Transaction' },
    { value: 'borrower_private', label: 'Borrower private' },
    { value: 'partner_supplied', label: 'Partner supplied' },
  ],
}

const AVAILABILITY_STYLE: Record<DocumentAvailability, string> = {
  available: 'bg-emerald-100 text-emerald-800',
  scanning: 'bg-amber-100 text-amber-800',
  awaiting_upload: 'bg-neutral-200 text-neutral-500',
  quarantined: 'bg-red-100 text-red-800',
  rejected: 'bg-red-100 text-red-800',
  deleted: 'bg-neutral-200 text-neutral-500',
}

const AVAILABILITY_LABEL: Record<DocumentAvailability, string> = {
  available: 'Clean',
  scanning: 'Scanning…',
  awaiting_upload: 'Upload incomplete',
  quarantined: 'Quarantined',
  rejected: 'Rejected',
  deleted: 'Deleted',
}

function AvailabilityBadge({ availability }: { availability: DocumentAvailability }) {
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${AVAILABILITY_STYLE[availability]}`}>
      {AVAILABILITY_LABEL[availability]}
    </span>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Case documents for whichever audience is signed in. The list is already projected server-side
 * (dealers never receive borrower-private rows), so this component only renders what it is given.
 * Anything that is not `available` has no "Open" — quarantine is enforced by the API, not here.
 */
export function DocumentsCard({ caseId, audience, canUpload }: { caseId: string; audience: Audience; canUpload: boolean }) {
  const docs = useDocuments(caseId)
  const [showUpload, setShowUpload] = useState(false)

  return (
    <Card
      title="Documents"
      actions={
        canUpload ? (
          <Button variant="secondary" onClick={() => setShowUpload((v) => !v)}>
            {showUpload ? 'Cancel' : 'Upload'}
          </Button>
        ) : undefined
      }
    >
      {showUpload && canUpload && <UploadForm caseId={caseId} audience={audience} onDone={() => setShowUpload(false)} />}

      {docs.isPending ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : docs.isError ? (
        <Alert tone="error">{errorMessage(docs.error)}</Alert>
      ) : docs.data.documents.length === 0 ? (
        <p className="text-sm text-neutral-500">No documents yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 text-sm">
          {docs.data.documents.map((d) => (
            <DocumentRow key={d.id} caseId={caseId} doc={d} />
          ))}
        </ul>
      )}
    </Card>
  )
}

function DocumentRow({ caseId, doc }: { caseId: string; doc: DocumentView }) {
  const access = useAccessDocument(caseId)
  const remove = useDeleteDocument(caseId)
  const [error, setError] = useState<string | null>(null)

  const open = async () => {
    setError(null)
    // Open synchronously so the popup is not blocked, then point it at the short-lived URL.
    // (`noopener` would make window.open return null, so detach the opener by hand.)
    const tab = window.open('about:blank', '_blank')
    if (tab) tab.opener = null
    try {
      const { access: a } = await access.mutateAsync(doc.id)
      if (tab) tab.location.href = a.url
      else window.location.assign(a.url)
    } catch (err) {
      tab?.close()
      setError(errorMessage(err))
    }
  }

  const del = async () => {
    if (!window.confirm(`Delete ${doc.original_filename}? Tex keeps the record for audit.`)) return
    setError(null)
    try {
      await remove.mutateAsync(doc.id)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const quarantined = doc.availability === 'quarantined'
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      <AvailabilityBadge availability={doc.availability} />
      <span className="font-medium">{doc.original_filename}</span>
      <span className="text-xs text-neutral-500">
        {humanize(doc.document_type)} · {humanize(doc.classification)} · v{doc.version} · {formatBytes(doc.size_bytes)} · {formatDate(doc.created_at)}
      </span>
      <span className="ml-auto flex items-center gap-2">
        {doc.availability === 'available' && (
          <Button variant="secondary" onClick={open} disabled={access.isPending}>
            Open
          </Button>
        )}
        {doc.can_delete && (
          <Button variant="danger" onClick={del} disabled={remove.isPending}>
            Delete
          </Button>
        )}
      </span>
      {quarantined && (
        <p className="basis-full text-xs text-red-700">
          This file failed or did not pass virus scanning and cannot be opened, exported or sent. Upload a clean copy as a new version.
          {doc.audience === 'tex' && doc.scan_signature && (
            <>
              {' '}
              Signature: <Mono>{doc.scan_signature}</Mono>
            </>
          )}
        </p>
      )}
      {doc.availability === 'scanning' && <p className="basis-full text-xs text-neutral-500">Virus scan in progress; the file opens once it is clean.</p>}
      {doc.availability === 'rejected' && (
        <p className="basis-full text-xs text-red-700">The uploaded bytes did not match the declared file (size, hash or type); nothing was kept.</p>
      )}
      {error && (
        <p className="basis-full">
          <Alert tone="error">{error}</Alert>
        </p>
      )}
    </li>
  )
}

function UploadForm({ caseId, audience, onDone }: { caseId: string; audience: Audience; onDone: () => void }) {
  const upload = useUploadDocument(caseId)
  const options = UPLOADABLE[audience]
  const [file, setFile] = useState<File | null>(null)
  const [documentType, setDocumentType] = useState('supporting_document')
  const [classification, setClassification] = useState<DocumentClassification>(options[0]?.value ?? 'transaction')
  const [progress, setProgress] = useState<UploadProgress | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!file) {
      setError('Choose a file first')
      return
    }
    setError(null)
    try {
      const doc = await upload.mutateAsync({ file, document_type: documentType, classification, onProgress: setProgress })
      setProgress(null)
      if (doc.availability === 'available' || doc.availability === 'scanning') onDone()
      else setError(`Upload finished but the file is ${AVAILABILITY_LABEL[doc.availability].toLowerCase()}.`)
    } catch (err) {
      setProgress(null)
      setError(errorMessage(err))
    }
  }

  const busy = upload.isPending
  return (
    <form onSubmit={submit} className="mb-4 space-y-3 rounded-md border border-[var(--border-subtle)] bg-neutral-50 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Document type" hint="snake_case, e.g. invoice, bank_statement">
          <Input
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            pattern="[a-z][a-z0-9_]{1,63}"
            required
            disabled={busy}
          />
        </Field>
        <Field label="Who can see it">
          <Select value={classification} onChange={(e) => setClassification(e.target.value as DocumentClassification)} disabled={busy}>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="File" hint="PDF, PNG, JPEG, XLSX or CSV, up to 25 MB. Files are virus-scanned before anyone can open them.">
        <Input type="file" accept={DOCUMENT_MIME_TYPES.join(',')} required disabled={busy} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </Field>
      {progress && (
        <div className="text-xs text-neutral-500">
          {progress.phase === 'authorizing' && 'Requesting upload slot…'}
          {progress.phase === 'uploading' && (
            <>
              Uploading {Math.round(progress.sent * 100)}%
              <progress className="ml-2 h-2 w-40 align-middle" value={progress.sent} max={1} />
            </>
          )}
          {progress.phase === 'verifying' && 'Verifying and scanning…'}
        </div>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>
          {busy ? 'Uploading…' : 'Upload'}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
